import { Component, ElementRef, EventEmitter, OnInit, OnDestroy, ViewChild } from "@angular/core";
import {
    TCITenantSelectorType,
    TCITenantAppType,
    ITCISupplement,
    TCITenantAppCategory,
    ITCITenantAppCategory,
    SupplementColResMap,
    SupplementTypeMetaData,
    ITCISupplementTypeMetaDataOption,
    ITCITenantSupplementCreateUIBindingButton,
    TCI_CREATE_SUPPLEMENT_UI_TYPE,
    ITCIFile,
    ITCIFileMap,
    ITCISupplementUploadSupportedFile,
    ITCISupplementUploadFileCheckResult,
    supportedSupplementFileSpecs,
    ITCISupplementConnector,
    ITCISupplementServiceProvider,
    ITCITenantSupplementRequest,
    ITCIValidationResult,
    implementsTKeys,
    TCIActionState,
    TCITenantValidationResult,
    ITCITenantSupplementResponse,
    USER_ROLES,
    Guid
} from "@tci/common";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { ITCICEnvToolsComponentOutput } from "../../ng-tenant/tci-tenant-components";
import { AbstractTCITenantComponent, InlineMenuItemHandlerFunction } from "../../ng-tenant/tci-tenant-component";
import { TCITenantMenuServices } from "../../ng-tenant/tci-tenant.menu";
import { TciTenantModelService } from "../../ng-tenant/tci-tenant.model";
import { TciTenantService } from "../../ng-tenant/tci-tenant.service";
import { NgAppFeatureToggleService } from "../../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgEnvToolsComponentInput } from "../tool-loader/ng-env-tools-loader.component";
import { BaseSupplementListService, ISupplementsMenuOpts } from "./base-supplement-list.service";
import { getCategoryFilterOptions, ICategoryFilterOptions } from "../../ng-shared/ng-util/tools";
import { NgPaginatorComponent } from "../../ng-shared/ng-components/ng-paginator/ng-paginator.component";
import { Observable, BehaviorSubject, Subscription } from "rxjs";
import * as lodash from "lodash";
import { Dialog, SelectItem } from "primeng/primeng";
import { UploadEvent } from "ngx-file-drop";
import * as CryptoJS from "crypto-js";

const FILTER_LABEL_ALL = "All";
const SUPP_LOCAL_ITEM = "TCI-SUPP-LOCAL-DATA";
const SUPP_LOCAL_ITEM_PREFIX = "tci_supp_";
const ENCODING_STR = "Binary";

// For supplement meta and actions info
enum SUPPLEMENTS_META {
    CAN_UPDATE = "canUpdate",
    CAN_DELETE = "canDelete",
    CATEGORY = "category",
    CONNECTED_APPS = "connectedApps",
    MENU_OPTIONS = "statusMenuOptions",
    MENU_OPTIONS_AVAILABLE = "statusMenuAvailable",
}

interface ITenantSupplementCreateOpt {
    type: TCITenantAppType;
    option: ITCISupplementTypeMetaDataOption;
}

interface ISupplementCategoryGroup {
    category: TCITenantAppCategory;
    options: ITenantSupplementCreateOpt[];
}

interface IStoredFilter {
    filter: { category: string };
    sort: { column: string, asc: boolean };
    page: number;
}

export class OptionSuppBtnStyle {
    public static DEFAULT = "btn-default";
    public static PRIMARY = "btn-primary";
}

interface ITempTenantOptBtn extends ITCITenantSupplementCreateUIBindingButton {
    uiLabel: string;
    uiStyle: OptionSuppBtnStyle;
}

@Component({
    selector: TCITenantSelectorType.TCI_ENV_TOOLS_SUPPLEMENTS.toString(),
    templateUrl: "./base-supplement.component.html",
    // Note: multiple css file in styleUrls MUST keep it in one line, otherwise sourcemap will be in disorder when debug in browser.
    styleUrls: ["../ng-env-tools.component.css", "./base-supplement.component.css"]
})
export class BaseSupplementComponent
    extends AbstractTCITenantComponent<EventEmitter<string>, NgEnvToolsComponentInput, ITCICEnvToolsComponentOutput<any>>
    implements OnInit, OnDestroy {

    @ViewChild("pager") pager: NgPaginatorComponent;
    @ViewChild("dialogDeleteSupplement") dialogDeleteSupplement: Dialog;
    @ViewChild("dialogUploadSupplement") dialogUploadSupplement: Dialog;
    @ViewChild("inputFiles") FilesInput: ElementRef;

    private subscriptions: Subscription[] = [];
    private supplements: ITCISupplement[] = [];
    /* private deleteSupplementModalShow = false;
    private deleteSupplementInfo: ITCISupplement; */
    isLoading = true;
    private canShowUploadSupplementBtn = false;
    private currentUserId: string;

    // Filter handlers
    private FILTER_CATEGORIES: ICategoryFilterOptions[] = [];
    private filteredSupp: ITCISupplement[] = [];
    private categoryFilter: string = FILTER_LABEL_ALL;

    // Sorting
    private SORTABLE_ROWS = SupplementColResMap.getSortableRows();
    private sortedBy: string;
    private sortAsc = false;

    // Pagination
    private pageStart = 0;
    private pages = 10;
    private paginatedRows$: BehaviorSubject<ITCISupplement[]> = new BehaviorSubject([]);
    private pageRestoreValue: number;

    // Status menu (HM)
    showStatusMenuRow: string;
    statusMenuOptionsInlineFns: { [key: string]: InlineMenuItemHandlerFunction<any, any, any> } = {};

    // Upload Supplement
    protected showUploadSupplementModal = false;
    protected wizStep = -1;
    protected categoryOptions: ISupplementCategoryGroup[] = [];
    protected ready = false;
    protected defaultSelectedOption: ITenantSupplementCreateOpt;
    protected selectedOption: ITenantSupplementCreateOpt;
    protected selectedOptionBtns: ITempTenantOptBtn[] = [];
    protected selectedTenantProv: ITCISupplementServiceProvider;
    protected inputReq: ITCITenantSupplementRequest = {};
    protected canCreate = false;
    protected selectedBtn: ITCITenantSupplementCreateUIBindingButton;
    protected allowedFiles: ITCISupplementUploadSupportedFile[] = [];
    protected allowToPublish = false;
    protected files: ITCIFileMap = {};
    protected selectedFile: { extension?: string, name?: string } = {};
    protected droppedFiles: { [key: string]: ITCISupplementUploadFileCheckResult } = {};
    protected errorMsg: string;
    protected connectorList: SelectItem[] = [];
    protected selectedConnector: string;
    protected dropDownLabel: string;

    constructor(
        private suppSvc: BaseSupplementListService,
        private toastr: NgToastrService,
        private tenantMenuSrv: TCITenantMenuServices,
        private tenantModelSvc: TciTenantModelService,
        private tenantSvc: TciTenantService,
        private featureToggleSvc: NgAppFeatureToggleService
    ) {
        super();
    }

    ngOnInit() {
        const setReady = () => {
            this.ready = true;
        };

        if (!this.tenantSvc.isInit()) {
            this.subscriptions.push(
                this.tenantSvc.onInit().subscribe(() => setReady())
            );
        } else {
            setReady();
        }

        this.subscriptions.push(
            this.input.getUserInfo()
                .finally(() => {
                    this.initializeSupplementsList();
                })
                .subscribe(userInfo => {
                    this.currentUserId = userInfo.userId;
                    this.inputReq.userInfo = userInfo;
                    if (userInfo.rolX[0] === USER_ROLES.ADM) {
                        this.canShowUploadSupplementBtn = true;
                    }
                })
        );
    }

    private initializeSupplementsList() {
        this.subscriptions.push(
            this.suppSvc.getSupplements()
            .subscribe(supplements => {
                this.supplements = lodash.uniqBy(supplements, "supplementId");

                this.generateLeftSideFilters();
                this.initStatus();
                /**
                 * Supplements initial release does not support app usage and action button (delete, HM menu).
                 * this.initStatusMenuOptions();
                 */
                const localStorageKey = this.getSuppPrefForUsr(this.currentUserId);
                const suppStageData = localStorage.getItem(localStorageKey);
                if (!lodash.isEmpty(suppStageData)) {
                    try {
                        const suppStage: IStoredFilter = JSON.parse(suppStageData);

                        // Restore sort
                        this.sortedBy = suppStage.sort.column;
                        this.sortAsc = suppStage.sort.asc;

                        // Restore pagination
                        this.pageRestoreValue = suppStage.page;

                        // Restore filter by category
                        const category = suppStage.filter.category;
                        const suppCategory = TCITenantAppCategory.fromId(category);
                        if (category !== FILTER_LABEL_ALL && !lodash.isUndefined(suppCategory) && !lodash.isEmpty(suppCategory)) {
                            // reset filter
                            this.categoryFilter = FILTER_LABEL_ALL;
                            this.filterByCategory(suppCategory);
                        } else {
                            this.filterSupp();
                        }
                    } catch (err) {
                        console.error("Supplement stage restore incomplete", err);
                        this.filterSupp();
                    }
                } else {
                    this.filterSupp();
                }
                // Hide loading spinner
                this.isLoading = false;
            })
        );
    }

    private get showSupplements(): boolean {
        return this.supplements.length > 0;
    }

    private generateLeftSideFilters() {
        this.FILTER_CATEGORIES = getCategoryFilterOptions(FILTER_LABEL_ALL)
        .map(filterOpt => {
            if (filterOpt.category.id === FILTER_LABEL_ALL) {
                filterOpt.counter = this.supplements.length;
            } else {
                filterOpt.counter = this.supplements
                    .filter(supplement => {
                        supplement.tenantType = TCITenantAppType.fromJSON(supplement.tenantType);
                        return TCITenantAppCategory.fromSupplement(supplement).id === filterOpt.category.id;
                    })
                    .length;
            }
            return filterOpt;
        })
        .filter(opt => opt.counter > 0);
    }

    private initStatus() {
        this.supplements.forEach(supp => {
            // const tenant = this.tenantModelSvc.getTenant(supp.tenantType);
            // const provider = tenant.getSupplementServiceProvider();

            supp[SUPPLEMENTS_META.CATEGORY] = TCITenantAppCategory.fromSupplement(supp).label;

            /* if (provider) {
                this.subscriptions.push(
                    Observable.combineLatest(
                        provider.canUpdate(this.getSupplementRequest(supp)),
                        provider.canDelete(this.getSupplementRequest(supp)),
                        provider.getConnectedAppsById(this.getSupplementRequest(supp)),
                        (canUpdate, canDelete, connectedApps) =>
                            ({canUpdate, canDelete, connectedApps})
                    ).subscribe(res => {
                        supp[SUPPLEMENTS_META.CAN_UPDATE] = res.canUpdate;
                        supp[SUPPLEMENTS_META.CAN_DELETE] = res.canDelete;
                        supp[SUPPLEMENTS_META.CONNECTED_APPS] = (res.connectedApps || []).length;
                    })
                );
            } else {
                console.warn(`No supplement provider for this supplement type: ${supp}`);
            } */
        });
    }

    /* private initStatusMenuOptions() {
        this.suppSvc.statusMenuOptions$.subscribe(opts => {
            opts.forEach(opt => {
                const POS = lodash.findIndex(this.supplements, {supplementId: opt.supplement.supplementId});
                if (POS > -1) {
                    const FOUND_SUPP = this.supplements[POS];

                    if (FOUND_SUPP[SUPPLEMENTS_META.MENU_OPTIONS] && Array.isArray(FOUND_SUPP[SUPPLEMENTS_META.MENU_OPTIONS])) {
                        FOUND_SUPP[SUPPLEMENTS_META.MENU_OPTIONS].push(...opt.comps);
                        FOUND_SUPP[SUPPLEMENTS_META.MENU_OPTIONS_AVAILABLE] = true;
                    } else {
                        FOUND_SUPP[SUPPLEMENTS_META.MENU_OPTIONS] = opt.comps || [];
                        FOUND_SUPP[SUPPLEMENTS_META.MENU_OPTIONS_AVAILABLE] = opt.comps.length > 0;
                    }
                }
            });
            this.supplements.forEach(supp => {
                supp[SUPPLEMENTS_META.MENU_OPTIONS].forEach((opt: TCITenantEnvToolsSupplements) => {
                    const selStr = opt.getComponentSelectorType().toString();
                    // Identify inline components
                    const preloadMap = this.tenantMenuSrv.getTenantPreloadMap(supp.tenantType);
                    this.statusMenuOptionsInlineFns[selStr] = preloadMap[selStr] && preloadMap[selStr].inlineMenuHandlerFn;
                });
            });
        });
    } */

    /* private getSupplementRequest(supp: ITCISupplement): ITCITenantSupplementRequest {
        return {
            supplementId: supp.supplementId // Add any required properties in the request here.
        };
    } */

    private filterByCategory(opt: ITCITenantAppCategory) {
        const filteredCategory: string = opt.id === FILTER_LABEL_ALL ? FILTER_LABEL_ALL : opt.id;
        if (this.categoryFilter !== filteredCategory) {
            this.categoryFilter = filteredCategory;
            this.filterSupp();
        }
    }

    private filterSupp() {
        this.suppSvc.getCurrentRemoteServices()
            .map(svcs => svcs.map(t => t.toString()))
            .subscribe(svcs => {
                console.group(">> Supplements");
                this.filteredSupp = this.supplements
                    .filter(supp => svcs.includes( supp.tenantType.toString()))
                    .filter(supp =>
                        this.categoryFilter === FILTER_LABEL_ALL ||
                        TCITenantAppCategory.fromSupplement(supp).id === this.categoryFilter
                    );
                this.sort(this.sortedBy || SupplementColResMap.getSortableRows().SUPPLEMENTED_FOR);
                console.log("Services", svcs);
                console.log("Records", this.filteredSupp);
                console.groupEnd();
            });
    }

    // <Paginator instructions>
    private setPaginatedRows() {
        const part = this.filteredSupp.slice(this.pageStart, this.pageStart + this.pages);
        if (part) {
            if (part.length === 0 && this.pager) {
                const prevPage = this.pager.getPage() - 1;
                if (prevPage > 0) {
                    this.pager.changePage(prevPage);
                } else {
                    this.paginatedRows$.next(part); // Refresh list when delete last entry
                }
            } else {
                this.paginatedRows$.next(part);
            }
        }
        this.storeFilterStage();
    }

    private onPageChange($event: number) {
        this.pageStart = $event;
        this.setPaginatedRows();
    }

    // <Sorting instructions>
    private sort(sortBy: string) {
        if (sortBy === SupplementColResMap.getSortableRows().SUPPLEMENTED_FOR) {
            this.filteredSupp = this.filteredSupp.sort((a, b) =>
            this.alphaNumSort(a.connector.name.toString(), b.connector.name.toString())).reverse();
        } else {
            this.filteredSupp = lodash.sortBy(this.filteredSupp, SupplementColResMap.getKey(sortBy)).reverse();
        }
        if (!this.sortAsc) { // If sorting is DESC then reverse
            this.filteredSupp.reverse();
        }

        this.setPaginatedRows();
    }

    private toggleSort(sortBy: string) {
        this.sortAsc = !this.sortAsc;
        this.sortedBy = sortBy;
        if (sortBy === SupplementColResMap.getSortableRows().SUPPLEMENTED_FOR) {
            this.filteredSupp = this.filteredSupp.sort((a, b) =>
            this.alphaNumSort(a.connector.name.toString(), b.connector.name.toString())).reverse();
        } else {
            this.filteredSupp = lodash.sortBy(this.filteredSupp, SupplementColResMap.getKey(sortBy)).reverse();
        }

        if (!this.sortAsc) {
            this.filteredSupp.reverse();
        }
        this.setPaginatedRows();
        this.pager.changePage(1);
    }

    private alphaNumSort(comparison1: string, comparison2: string) {
        return comparison1.localeCompare(comparison2, "en", { numeric: true, caseFirst: "upper" });
    }

    private storeFilterStage() {
        const filters: IStoredFilter = {
            filter: {
                category: this.categoryFilter
            },
            sort: {
                column: this.sortedBy,
                asc: this.sortAsc
            },
            page: this.pager ? this.pager.getPage() : 1
        };
        const localStorageKey = this.getSuppPrefForUsr(this.currentUserId);
        localStorage.setItem(localStorageKey, JSON.stringify(filters));
    }

    private getSuppPrefForUsr(userId: string): string {
        return SUPP_LOCAL_ITEM_PREFIX + CryptoJS.SHA256(SUPP_LOCAL_ITEM + userId).toString();
    }
    /* private modalCenterDeleteSupplement() {
        this.dialogDeleteSupplement.center();
    } */

    /* private deleteSupplementConfirm(agent: ITCISupplement) {
        this.deleteSupplementInfo = agent;
        this.deleteSupplementModalShow = true;
        this.toastr.clear();
    } */

    /* private deleteCancel() {
        this.deleteSupplementInfo = undefined;
        this.deleteSupplementModalShow = false;
    } */

    /* private deleteSupplement() {
        // TODO: Implement supplements delete here.
    } */

    private toggleStatusMenu($event: MouseEvent, supp: ITCISupplement) {
        this.showStatusMenuRow = (this.showStatusMenuRow !== supp.supplementId) ? supp.supplementId : undefined;
        $event.stopPropagation();
    }

    /* private executeInlineCompFn($event: MouseEvent, opt: TCITenantEnvToolsSupplements, supp: ITCISupplement) {
        const selector = opt.getComponentSelectorType().toString();

        const supplementMenuInput = new BaseEnvToolsSupplementMenuComponentInput(
            this.userService,
            this.featureToggleSvc,
            opt
        );
        const injector = this.tenantMenuSrv.getTenantInjector(supp.tenantType);
        this.statusMenuOptionsInlineFns[selector](opt, supplementMenuInput, injector);
        this.toggleStatusMenu($event, supp);
    } */

    /* private mountStatusMenuCompFn($event: MouseEvent, opt: TCITenantEnvToolsSupplements, supp: ITCISupplement) {
        this.toggleStatusMenu($event, supp);
        console.log("mountStatusMenuCompFn called");
        // TODO: Implement supplements non-inline HM options (ex. Edit) call here.
    } */

    private openUploadSupplementModal() {
        this.showUploadSupplementModal = true;
        this.setSupplementCreateOptions();
    }

    private uploadModalAfterShow() {
        this.dialogUploadSupplement.center();
        this.initFilesInput();
    }

    private setSupplementCreateOptions() {
        this.subscriptions.push(
            this.suppSvc.getCurrentRemoteServices()
            .map(svcs => svcs.map(t => t.toString()))
            .subscribe(svcs => {
                const cats: ISupplementCategoryGroup[] = [];
                SupplementTypeMetaData.getAllItems()
                    .filter(optionGroup => optionGroup.enabled && svcs.includes(optionGroup.type.toString()))
                    .forEach(optionGroup => {
                        let cat: ISupplementCategoryGroup = cats.find(_cat => _cat.category.label === optionGroup.category.label);
                        if (!cat) { // New category
                            cat = {
                                category: optionGroup.category,
                                options: []
                            };
                            cats.push(cat);
                        }

                        cat.options.push(...optionGroup.options.map(option => {
                            return <ITenantSupplementCreateOpt>{
                                type: optionGroup.type,
                                option
                            };
                        }));
                    });

                this.categoryOptions = lodash.sortBy(cats, "category.order");
                this.wizStep++;

                // Select first option in categoryOptions as default.
                if (this.categoryOptions.length > 0) {
                    const firstCategory = this.categoryOptions[0];
                    this.defaultSelectedOption = {...firstCategory.options[0]};
                    this.selectOption(this.defaultSelectedOption.type, this.defaultSelectedOption.option);
                }
            })
        );
    }

     // Generate buttons to display
     setButtonsLabels() {
        this.selectedOptionBtns = [];
        this.selectedOption.option.buttons.forEach(_btn => {
            const btn: ITempTenantOptBtn = lodash.cloneDeep(_btn as any);
            btn.uiStyle = OptionSuppBtnStyle.DEFAULT;
            btn.uiLabel = btn.label;
            this.selectedOptionBtns.push(btn);
            /**
             * As per discussions, No wizard step 1 for upload UI.
             * Both connector select and upload element is on wizard step 0
             */
            // if (
            //     this.selectedOption.option.buttons.length === 1 &&
            //     [TCI_CREATE_SUPPLEMENT_UI_TYPE.FILE_UPLOAD].includes(btn.uiType)
            // ) {
            //     btn.uiLabel = "Next";
            //     this.selectedOptionBtns.push(btn);
            // } else {
            //      btn.uiLabel = btn.label;
            //      this.selectedOptionBtns.push(btn);
            // }
        });

        lodash.last(this.selectedOptionBtns).uiStyle = OptionSuppBtnStyle.PRIMARY;
    }

    selectOption(type: TCITenantAppType, option: ITCISupplementTypeMetaDataOption) {
        this.wizStart();
        this.selectedOption = { option, type };
        this.setButtonsLabels();
        this.resetFiles();
        this.selectedConnector = undefined;
        // reset Upload button state.
        this.resetUploadBtnState();
        this.errorMsgHide();
        this.inputReq = { userInfo: {...this.inputReq.userInfo} };
        this.dropDownLabel = option.dropDownLabel;
        // Load connector dropdown list
        this.loadConnectorList(type);
        this.subscriptions.push(
            this.tenantSvc.onInit()
                .subscribe(() => {
                    const tenant = this.tenantModelSvc.getTenant(this.selectedOption.type);
                    if (tenant) {
                        const provider = tenant.getSupplementServiceProvider();
                        if (provider) {
                            this.selectedTenantProv = provider;
                        }
                    }
                })
        );
    }

    private loadConnectorList(tenantType: TCITenantAppType) {
        // Load tenant specific supplement connector list.
        this.subscriptions.push(
            this.suppSvc.getConnectorList(tenantType)
                .subscribe((connectors: ITCISupplementConnector[]) => {
                    this.connectorList = connectors.map((conn: ITCISupplementConnector) => {
                        return {
                            label: conn.name,
                            value: conn.id
                        };
                    });
                })
        );
    }

    connectorSelectChange(event) {
        this.resetFiles();
        this.errorMsgHide();
    }

    optBtnAction(option: ITCITenantSupplementCreateUIBindingButton) {

        this.selectedBtn = option;
        if (this.wizStep === 0) {
            this.selectedOptionBtns = [];
            const btn: ITempTenantOptBtn = lodash.cloneDeep(option as any);
            btn.uiLabel = btn.label;
            btn.uiStyle = OptionSuppBtnStyle.PRIMARY;
            this.selectedOptionBtns.push(btn);

            if (this.canCreate && this.allowToPublish) {
                this.isLoading = true;
                this.subscriptions.push(
                    this.suppSvc.uploadSupplement(this.inputReq)
                        .subscribe((res: ITCITenantSupplementResponse) => {
                            if (res.success) {
                               const suppSubscription = this.suppSvc.getSupplementStatus(this.selectedTenantProv)
                                    .subscribe(suppStatus => {
                                        if (suppStatus.success) {
                                            this.isLoading = false;
                                            // Show success toastr message
                                            this.toastr.success(suppStatus.msg);
                                            // Close upload modal
                                            this.cancel();
                                            // Unsubscribe status subject.
                                            suppSubscription.unsubscribe();
                                            // Re-initialize supplements list
                                            this.initializeSupplementsList();
                                        } else {
                                            this.toastr.error(suppStatus.msg);
                                            this.isLoading = false;
                                            console.error("Supplement status error : ", suppStatus);
                                        }
                                    });
                            } else {
                                this.toastr.error(res.msg);
                                this.isLoading = false;
                                console.error("Supplement upload error : ", res);
                            }
                        }, err => {
                            console.error("Supplement upload error : ", err);
                            this.isLoading = false;
                        })
                );
            }
            // this.wizNext();
        }
    }

    /* wizNext() {
        this.wizStep++;
    } */

    /* wizBack() {
        this.wizStep--;
        this.setButtonsLabels();
    } */

    wizStart() {
        this.wizStep = 0;
    }

    cancel() {
        this.showUploadSupplementModal = false;
        this.selectedOptionBtns = [];
        // Clear uploaded file.
        this.resetFiles();
        // Reset selected connector value.
        this.selectedConnector = undefined;
        // reset Upload button state.
        this.resetUploadBtnState();
        // Rest input req
        this.inputReq = { userInfo: {...this.inputReq.userInfo} };
        // Reset error message.
        this.errorMsgHide();
    }

    protected initFilesInput() {
        this.allowedFiles = supportedSupplementFileSpecs();
    }

    protected resetFiles() {
        this.selectedFile = {};
        this.allowedFiles.forEach(f => this.removeFile(f));
    }

    protected dropped(event: UploadEvent) {
        const files: File[] = [];
        let processed = 0;
        event.files
            .filter(f => f.fileEntry.isFile)
            .forEach(f => {
                f.fileEntry.file((fe: File) => {
                    files.push(fe);

                    processed++;
                    if (processed === event.files.length) {
                        this.handleSelectedFiles(files);
                    }
                });
            });
    }

    protected droppedFilesValidate(files: ITCISupplementUploadFileCheckResult[]) {
        this.allowToPublish = false;
        this.errorMsgHide();

        this.inputReq.tenantType = this.selectedOption.type;
        this.inputReq.connectorId = this.selectedConnector;
        this.inputReq.name = this.selectedFile.name || "";
        if (this.selectedTenantProv) {
            this.subscriptions.push(
                Observable.combineLatest(
                    this.selectedTenantProv.canCreate(this.inputReq),
                    this.selectedTenantProv.validate(this.inputReq),
                    (canCreate, validate) =>
                        ({canCreate, validate})
                ).subscribe(res => {
                    // Evaluate canCreate response.
                    if (res.canCreate) {
                        const canCreateResult: ITCIValidationResult | boolean = res.canCreate;
                        let canCreateResVal: ITCIValidationResult;
                        if (implementsTKeys<ITCIValidationResult>(
                            canCreateResult,
                            ["errors", "getType", "setError", "getErrors", "state"]
                        )) {
                            canCreateResVal = canCreateResult;
                            const errors = canCreateResVal.getErrors();
                            if (canCreateResVal.state === TCIActionState.TCI_ACTION_OK || errors.length === 0) {
                                this.canCreate = true;
                            } else {
                                this.canCreate = false;
                            }
                        } else if (lodash.isEqual(canCreateResult, true)) {
                            this.canCreate = true;
                        } else {
                            this.canCreate = false;
                        }
                    } else {
                        this.canCreate = false;
                        console.error(`No tenant response for canCreate supplements`);
                    }

                    // Evaluate validate response.
                    if (res.validate) {
                        const tenantValidationResult = new TCITenantValidationResult(res.validate);
                        if (tenantValidationResult.isOK()) {
                            this.allowToPublish = true;
                            this.errorMsgHide();
                        } else if (tenantValidationResult.isWarning()) {
                            this.allowToPublish = false;
                            const warnings = tenantValidationResult.getWarnings().map(w => w.getErrorMessage());
                            console.warn("Supplement upload returned warning : ", warnings);
                            this.toastr.warn(warnings.toString()); // warnings are returned as an array.
                        } else {
                            this.allowToPublish = false;
                            const errors = tenantValidationResult.getErrors().map(e => e.getErrorMessage());
                            console.error("Supplement upload file validation error.", errors);
                            this.toastr.error(errors.toString()); // errors are returned as an array.
                        }
                    } else {
                        this.allowToPublish = false;
                        console.error(`No tenant response for validate supplements`);
                    }
                })
            );
        } else {
            console.warn(`No supplement provider for tenant : ${this.selectedOption.type}`);
        }
    }

    protected handleFilesChange(event: any) {
        const files = <File[]>lodash.toArray(event.target.files);
        this.handleSelectedFiles(files);
    }

    protected handleSelectedFiles(files: File[]) {
        if (files.length === 0) {
            return;
        }

        this.errorMsgHide();
        const emitErr = (msg: string) => {
            this.errorMsg = msg;
            if (lodash.size(this.droppedFiles) < this.allowedFiles.length) { // There'are already valid files
                this.allowToPublish = false;
            }
        };

        if (files.length > 1) {
            emitErr("There are more selected files than expected.");
        } else if (!this.allowedFiles.some(a => files.map(f => f.name).some(f => f.endsWith("." + a.extension)))) {
            emitErr("There are unsupported files in your selection.");
        } else if (lodash.some(lodash.countBy(files, file => lodash.last(file.name.split("."))), count => count > 1)) {
            emitErr(`Selected files have more than one ${this.allowedFiles.map(f => f.extension).join(" or ")}.`);
        } else {
            this.inputReq.appData = <ITCIFileMap>{};
            const fileCheck: Observable<ITCISupplementUploadFileCheckResult>[] = files.map(file => Observable.create(observer => {
                const name: string = file.name;
                const res: ITCISupplementUploadFileCheckResult = {
                    type: lodash.first(this.allowedFiles.filter(f => name.endsWith("." + f.extension))).extension,
                    fileContent: null, name, error: false, errorMsg: null
                };

                if (!this.files[res.type]) { this.files[res.type] = <any>{}; }
                this.files[res.type].content = "";
                /**
                 * Hold just file object in appData instead of file content.
                 * UNIQ_FILE_OBJ_KEY is important to represent every file uniquely in the tmp upload location
                    and deletion after upload to AO.
                 * # symbol is used as a marker to separate UUID from file name in WS backend.
                 */
                const UNIQ_FILE_OBJ_KEY = `${Guid.newGuid()}#${file.name}`;
                res.fileContent =  <ITCIFile>{
                    content: file, /* Assign file object instead of file contents. */
                    mimeType: file.type,
                    encoding: ENCODING_STR
                };
                this.inputReq.appData[UNIQ_FILE_OBJ_KEY] = res.fileContent;
                observer.next(res);
                observer.complete();
            }));
            this.subscriptions.push(
                Observable.merge(...fileCheck)
                    .toArray()
                    .subscribe(DroppedFiles => {
                        if (!DroppedFiles.some(fcr => fcr.error)) {
                            DroppedFiles.forEach(fr => {
                                this.files[fr.type] = fr.fileContent;
                                this.selectedFile = { extension: fr.type, name: fr.name };
                                this.droppedFiles[fr.type] = fr; // Feed the dropped files array
                            });
                            this.droppedFilesValidate(lodash.toArray(this.droppedFiles));
                        } else {
                            this.errorMsg = lodash.sample(DroppedFiles).errorMsg;
                            this.allowToPublish = false;
                        }
                })
            );
        }
    }

    protected removeFile(file: ITCISupplementUploadSupportedFile) {
        if (this.files[file.extension]) {
            this.errorMsgHide();
            this.FilesInput.nativeElement.value = "";
            this.files[file.extension] = {content: null, encoding: ENCODING_STR, mimeType: null};
            delete this.droppedFiles[file.extension];

            if (file.optional !== true) { // Disable push if not optional
                this.allowToPublish = false;
            }
        }
    }

    protected resetUploadBtnState() {
        // reset Upload button state.
        this.allowToPublish = false;
        this.canCreate = false;
    }

    protected isFileValid() {
        const fileExtn = this.selectedFile.extension;
        return this.files[fileExtn]
            && !lodash.isNull(this.files[fileExtn].mimeType)
            && !lodash.isEmpty(this.files[fileExtn].encoding);
    }

    public errorMsgHide() {
        this.errorMsg = undefined;
    }

    public setErrorMsg(msg: string) {
        this.errorMsg = msg;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
